import * as React from 'react';
import { useSelector } from 'react-redux';

import { debounce } from 'throttle-debounce';

import { placeholders } from './constants/placeholders';
import { useHandleSuggestItemClick } from './useHandleSuggestItemClick';
import { useActions } from '../../useActions';

import { selectFormFields, selectEstimationTarget, selectTargetPage } from '../../../selectors';

import { IAddressInputProps } from 'shared/common/utils/types';
import { EMyHomeEstimationTargets } from '../../../types/estimationTarget';
import { ECurrentErrorField } from '../../../types/formFields';

import { trackMyHomeAddressInputClick, trackMyHomeAddressInputLaterSomeSeconds } from '../../tracking';

let timerIdForAddressInputLaterSomeSeconds: number | undefined;
const timeoutAddressInputLater = 5000;

export const useAddressProps = (): IAddressInputProps => {
  const { address, errorTooltip, addressGeoSuggest } = useSelector(selectFormFields);
  const estimationTarget = useSelector(selectEstimationTarget);
  const targetPage = useSelector(selectTargetPage);

  const { setAddress, getFlatAreasSuggest, getFlatGeoSuggest } = useActions();

  /** Если есть ошибка, отобразится над инпутом фильтра */
  const errorMessage = errorTooltip.currentErrorField === ECurrentErrorField.address ? errorTooltip.errorText : '';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getGeoSuggestDebounced = React.useCallback(debounce(200, getFlatGeoSuggest), []);

  /** Обработчик ввода адреса */
  const onValueChange = React.useCallback(
    (value: string) => {
      clearTimeout(timerIdForAddressInputLaterSomeSeconds);

      setAddress(value);
      getGeoSuggestDebounced({ addressChunk: value, targetPage });

      if (value) {
        timerIdForAddressInputLaterSomeSeconds = window.setTimeout(
          () => trackMyHomeAddressInputLaterSomeSeconds(value, estimationTarget),
          timeoutAddressInputLater,
        );
      }
    },
    [getGeoSuggestDebounced, trackMyHomeAddressInputLaterSomeSeconds, setAddress, estimationTarget, targetPage],
  );

  /** Обработчик клика по итему в геосаджесте (список предложенных адресов) */
  const handleItemClick = useHandleSuggestItemClick(errorMessage, timerIdForAddressInputLaterSomeSeconds);

  /** Обработчик клика по инпуту */
  const onInputTagClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') {
        trackMyHomeAddressInputClick(estimationTarget);
      }
    },
    [estimationTarget],
  );

  /** В момент 1й загрузки делается запрос за саджестом, если в сторе имеется адрес Квартиры */
  React.useEffect(() => {
    if (address && estimationTarget === EMyHomeEstimationTargets.flat) {
      getFlatAreasSuggest(address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    value: address,
    errorMessage,
    addressGeoSuggest,
    onInputTagClick,
    onValueChange,
    onItemClick: handleItemClick,
    placeholder: placeholders[estimationTarget],
  };
};
